import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { Minus, Plus, Check, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api, money } from "../lib/api";
import { useCart } from "../context/CartContext";
import { AcademySelect } from "../components/AcademySelect";
import { PreorderInfo } from "../components/PreorderBadge";
import { currentWindow } from "../components/Countdown";
import { RelatedProducts } from "../components/RelatedProducts";

const AUD_LABEL = { Adult: "Adult", Youth: "Youth", Women: "Women's", All: "Team Accessories" };

const SWATCH = {
  original: "linear-gradient(135deg,#10B981 0%,#10B981 45%,#FACC15 55%,#FACC15 100%)",
  "black-original": "linear-gradient(135deg,#111 0%,#111 50%,#10B981 50%,#FACC15 100%)",
  "black-white": "linear-gradient(135deg,#111 0%,#111 50%,#fff 50%,#fff 100%)",
  "white-black": "linear-gradient(135deg,#fff 0%,#fff 50%,#111 50%,#111 100%)",
  "black-tan": "linear-gradient(135deg,#111 0%,#111 50%,#C2B280 50%,#C2B280 100%)",
  "tan-black": "linear-gradient(135deg,#C2B280 0%,#C2B280 50%,#111 50%,#111 100%)",
  "pink-original": "linear-gradient(135deg,#ec4899 0%,#ec4899 50%,#10B981 50%,#FACC15 100%)",
  limited: "linear-gradient(135deg,#B8860B 0%,#111 60%)",
  "black-gold": "linear-gradient(135deg,#111 0%,#111 50%,#D4A017 50%,#D4A017 100%)",
  white: "#f4f4f5",
  black: "#0a0a0a",
  blue: "#1d4ed8",
  purple: "#7e22ce",
  brown: "#7c4a1e",
  pink: "#ec4899",
  green: "#10B981",
  gold: "linear-gradient(135deg,#FACC15 0%,#111 60%)",
};

function sizeSurcharge(size) {
  const s = (size || "").toUpperCase();
  const m = /^(\d+)XL$/.exec(s);
  if (m && parseInt(m[1], 10) >= 2) return (parseInt(m[1], 10) - 1) * 1.5;
  const g = /^A(\d)$/.exec(s);
  if (g && parseInt(g[1], 10) >= 5) return (parseInt(g[1], 10) - 4) * 1.5;
  return 0;
}

function useProductSeo(product, gallery) {
  useEffect(() => {
    if (!product) return;
    const title = product.seo_title || `${product.name} | Paragon BJJ`;
    document.title = title;
    const setMeta = (attr, key, val) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute("content", val || "");
    };
    setMeta("name", "description", product.meta_description || product.description);
    setMeta("property", "og:title", title);
    setMeta("property", "og:type", "product");
    setMeta("property", "og:image", gallery?.[0]?.url || product.images?.front || "");
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement("link"); canonical.setAttribute("rel", "canonical"); document.head.appendChild(canonical); }
    canonical.setAttribute("href", `${window.location.origin}/product/${product.id}`);
    const ld = {
      "@context": "https://schema.org", "@type": "Product", name: product.name, brand: { "@type": "Brand", name: "Paragon" },
      description: product.meta_description || product.description,
      image: (gallery || []).map((g) => g.url).filter(Boolean),
      sku: product.sku,
      offers: { "@type": "Offer", price: product.retail_price, priceCurrency: "USD", availability: "https://schema.org/PreOrder" },
    };
    let script = document.getElementById("product-jsonld");
    if (!script) { script = document.createElement("script"); script.id = "product-jsonld"; script.type = "application/ld+json"; document.head.appendChild(script); }
    script.textContent = JSON.stringify(ld);
    return () => { document.title = "Paragon Brazilian Jiu-Jitsu · Official Team Store"; };
  }, [product, gallery]);
}

const Personalization = ({ fields, value, onChange }) => (
  <div data-testid="personalization-section" className="mt-6 border border-yellow-400/30 bg-yellow-400/5 rounded-lg p-4">
    <p className="font-mono text-xs uppercase tracking-widest text-yellow-400 mb-3">Personalize It</p>
    {fields.map((f) => (
      <label key={f.key} className="block mb-3 last:mb-0">
        <span className="flex justify-between font-mono text-[11px] uppercase tracking-widest text-zinc-400 mb-1">
          <span>{f.label}{f.required ? " *" : ""}</span><span>{(value[f.key] || "").length}/{f.max_length}</span>
        </span>
        <input data-testid={`personalization-${f.key}`} maxLength={f.max_length} value={value[f.key] || ""}
          onChange={(e) => onChange({ ...value, [f.key]: e.target.value.toUpperCase() })}
          placeholder={f.key === "name" ? "YOUR NAME" : "CALIFORNIA"}
          className="w-full bg-[#121216] border border-white/15 rounded-sm px-3 py-2.5 text-base sm:text-sm text-white uppercase tracking-wider placeholder:text-zinc-600 focus:outline-none focus:border-yellow-400" />
        {f.help && <span className="block text-[11px] text-zinc-500 mt-1">{f.help}</span>}
      </label>
    ))}
  </div>
);

export default function ProductDetail() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const cwParam = params.get("cw");
  const { addItem, academy, setAcademy } = useCart();
  const [product, setProduct] = useState(null);
  const [color, setColor] = useState(null);
  const [colorwayKey, setColorwayKey] = useState(null);
  const [audience, setAudience] = useState("Adult");
  const [size, setSize] = useState(null);
  const [qty, setQty] = useState(1);
  const [custom, setCustom] = useState({});
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    api.get(`/products/${id}`).then(({ data }) => {
      setProduct(data);
      setSize(null);
      setQty(1);
      setCustom({});
      if (data.type === "variant") {
        const firstCw = (data.colorways || []).find((c) => c.key === cwParam && c.available)
          || (data.colorways || []).find((c) => c.available) || data.colorways?.[0];
        setColorwayKey(firstCw?.key || null);
        setAudience(data.audiences?.[0]?.key || "Adult");
      } else {
        setColor(data.colors?.[0] || null);
      }
    });
    window.scrollTo(0, 0);
    // eslint-disable-next-line
  }, [id]);

  const isVariant = product?.type === "variant";
  const colorwayObj = useMemo(() => (isVariant ? (product.colorways || []).find((c) => c.key === colorwayKey) : null), [isVariant, product, colorwayKey]);
  const audienceObj = useMemo(() => (isVariant ? (product.audiences || []).find((a) => a.key === audience) : null), [isVariant, product, audience]);
  const sizes = isVariant ? (audienceObj?.sizes || []) : (product?.sizes || []);
  const gallery = useMemo(() => {
    if (!product) return [];
    if (isVariant) return colorwayObj?.images?.length ? colorwayObj.images : [{ url: product.images?.front, alt: product.name }];
    const arr = [];
    ["front", "back", "side"].forEach((k) => { if (product.images?.[k]) arr.push({ url: product.images[k], alt: `${product.name} ${k} view` }); });
    return arr.length ? arr : [{ url: product.images?.front, alt: product.name }];
  }, [product, isVariant, colorwayObj]);

  useProductSeo(product, gallery);

  useEffect(() => { setActiveImg(0); if (size && !sizes.includes(size)) setSize(null); /* eslint-disable-next-line */ }, [colorwayKey, audience]);

  if (!product) return <div className="text-zinc-500 font-mono py-32 text-center">Loading…</div>;

  const unitPrice = isVariant
    ? ((audienceObj?.price ?? product.retail_price) + (product.extended_size_rule && size ? sizeSurcharge(size) : 0))
    : (size ? product.size_prices?.[size] ?? product.retail_price : product.retail_price);

  const handleAdd = () => {
    if (!size) return toast.error("Please select a size");
    if (isVariant && !colorwayObj?.available) return toast.error("This colorway is coming soon");
    if (!academy) return toast.error("Please select your academy");
    const fields = product.personalization || [];
    const missing = fields.find((f) => f.required && !(custom[f.key] || "").trim());
    if (missing) return toast.error(`Please enter ${missing.label}`);
    addItem({
      customization: fields.length ? Object.fromEntries(fields.map((f) => [f.key, (custom[f.key] || "").trim().toUpperCase()])) : null,
      product_id: product.id, name: product.name, image: gallery[0]?.url,
      color: isVariant ? colorwayObj.label : color,
      colorway: isVariant ? colorwayKey : null,
      audience: isVariant ? audience : null,
      size, qty, unit_price: unitPrice,
    });
    toast.success(`Added ${qty} × ${product.name} (${size}) to cart`);
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-8 py-8">
      <nav className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-zinc-500 mb-5">
        <Link to="/" className="hover:text-yellow-400">Home</Link><ChevronRight className="h-3 w-3" />
        <Link to={`/shop?category=${encodeURIComponent(product.category)}`} className="hover:text-yellow-400">{product.category}</Link><ChevronRight className="h-3 w-3" />
        <span className="text-zinc-300">{product.name}</span>
      </nav>

      <div className="grid lg:grid-cols-2 gap-10">
        <div>
          <div className="bg-[#0A0A0C] border border-white/10 rounded-xl overflow-hidden aspect-[3/2]">
            <img data-testid="product-main-image" src={gallery[activeImg]?.url} alt={gallery[activeImg]?.alt || product.name} className="w-full h-full object-contain" />
          </div>
          {isVariant && product.colorways.length > 1 && (
            <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
              {product.colorways.map((c) => (
                <button key={c.key} data-testid={`thumb-${c.key}`} onClick={() => setColorwayKey(c.key)} aria-label={c.label}
                  className={`shrink-0 h-16 w-24 rounded-md overflow-hidden border bg-[#0A0A0C] transition-colors ${colorwayKey === c.key ? "border-yellow-400" : "border-white/15 hover:border-white/40"}`}>
                  <img src={c.images?.[0]?.url} alt={c.images?.[0]?.alt || c.label} className="w-full h-full object-contain" loading="lazy" />
                </button>
              ))}
            </div>
          )}
          {isVariant && gallery.length > 1 && (
            <div className="flex gap-2 mt-3">
              {gallery.map((g, i) => (
                <button key={g.url} data-testid={`gallery-thumb-${i}`} onClick={() => setActiveImg(i)} aria-label={`View image ${i + 1}`}
                  className={`h-14 w-20 rounded-md overflow-hidden border bg-[#0A0A0C] ${activeImg === i ? "border-yellow-400" : "border-white/15"}`}>
                  <img src={g.url} alt={g.alt} className="w-full h-full object-contain" loading="lazy" />
                </button>
              ))}
            </div>
          )}
          {!isVariant && gallery.length > 1 && (
            <div className="flex gap-2 mt-3">
              {gallery.map((g, i) => (
                <button key={i} data-testid={`thumb-${i}`} onClick={() => setActiveImg(i)} className={`h-20 w-20 rounded-md overflow-hidden border ${activeImg === i ? "border-yellow-400" : "border-white/15"}`}>
                  <img src={g.url} alt={g.alt} className="w-full h-full object-contain" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="flex items-center gap-3 mb-3">
            <span className="font-mono text-[11px] uppercase tracking-widest text-zinc-500">{product.category}</span>
            <span className="font-mono text-[11px] uppercase tracking-widest text-yellow-400">{(product.audiences?.length > 1 ? "Adult & Youth" : AUD_LABEL[product.audience] || product.audience)}</span>
          </div>
          <h1 data-testid="product-title" className="font-heading font-black uppercase text-3xl lg:text-4xl text-white leading-none">{product.name}</h1>
          <div className="mt-4 flex items-baseline gap-3">
            <span data-testid="product-price" className="font-mono text-3xl font-bold text-yellow-400">{money(unitPrice)}</span>
            {product.extended_size_rule && <span className="text-xs text-zinc-500 font-mono">{product.category === "Gis" ? "A5+ sizes add surcharge" : "Extended sizes 2XL+ add surcharge"}</span>}
          </div>

          {product.preorder_round_id && (
            <div className="mt-4 bg-yellow-400/10 border border-yellow-400/30 rounded-lg p-3">
              <span className="font-mono text-[10px] uppercase tracking-widest bg-yellow-400 text-black px-2 py-0.5 rounded-sm font-bold">Paragon Winter '26 · {currentWindow()?.round || "Closed"}</span>
              <div className="mt-2"><PreorderInfo /></div>
            </div>
          )}

          {/* Colorway (variant) */}
          {isVariant && (
            <div className="mt-6">
              <p className="font-mono text-xs uppercase tracking-widest text-zinc-400 mb-2">Colorway: <span className="text-white">{colorwayObj?.label}</span></p>
              <div className="flex flex-wrap gap-3">
                {product.colorways.map((c) => (
                  <button key={c.key} data-testid={`colorway-${c.key}`} disabled={!c.available} onClick={() => setColorwayKey(c.key)}
                    className={`flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-full border transition-all ${colorwayKey === c.key ? "border-yellow-400" : "border-white/15"} ${!c.available ? "opacity-40 cursor-not-allowed" : "hover:border-white/40"}`}>
                    <span className="h-6 w-6 rounded-full border border-white/20" style={{ background: SWATCH[c.key] || "#333" }} />
                    <span className="text-sm text-white">{c.label}</span>
                    {!c.available && <span className="text-[9px] font-mono uppercase text-zinc-500">soon</span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Age / Fit (variant) */}
          {isVariant && product.audiences?.length > 1 && (
            <div className="mt-6">
              <p className="font-mono text-xs uppercase tracking-widest text-zinc-400 mb-2">Age / Fit</p>
              <div className="flex gap-2">
                {product.audiences.map((a) => (
                  <button key={a.key} data-testid={`audience-${a.key}`} onClick={() => setAudience(a.key)}
                    className={`px-5 py-2.5 rounded-sm text-sm border ${audience === a.key ? "border-yellow-400 text-yellow-400 bg-yellow-400/10" : "border-white/15 text-zinc-300 hover:border-white/40"}`}>
                    {a.key} · {money(a.price)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Colors (simple) */}
          {!isVariant && product.colors?.length > 0 && (
            <div className="mt-6">
              <p className="font-mono text-xs uppercase tracking-widest text-zinc-400 mb-2">Color: <span className="text-white">{color}</span></p>
              <div className="flex flex-wrap gap-2">
                {product.colors.map((c) => (
                  <button key={c} data-testid={`color-${c}`} onClick={() => setColor(c)}
                    className={`px-4 py-2 rounded-sm text-sm border ${color === c ? "border-yellow-400 text-yellow-400 bg-yellow-400/10" : "border-white/15 text-zinc-300 hover:border-white/40"}`}>{c}</button>
                ))}
              </div>
            </div>
          )}

          {/* Sizes */}
          <div className="mt-6">
            <p className="font-mono text-xs uppercase tracking-widest text-zinc-400 mb-2">Select Size</p>
            <div className="flex flex-wrap gap-2">
              {sizes.map((s) => (
                <button key={s} data-testid={`size-${s}`} onClick={() => setSize(s)}
                  className={`min-w-[52px] px-3 py-2.5 rounded-sm text-sm font-mono border transition-all ${size === s ? "border-yellow-400 text-black bg-yellow-400 font-bold" : "border-white/15 text-zinc-200 hover:border-white/40"}`}>
                  {s}
                  {!isVariant && product.size_prices?.[s] > product.retail_price && (
                    <span className="block text-[9px] opacity-70">+{money(product.size_prices[s] - product.retail_price)}</span>
                  )}
                  {isVariant && product.extended_size_rule && sizeSurcharge(s) > 0 && (
                    <span className="block text-[9px] opacity-70">+{money(sizeSurcharge(s))}</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Academy */}
          <div className="mt-6">
            <p className="font-mono text-xs uppercase tracking-widest text-zinc-400 mb-2">Which Paragon academy are you with?</p>
            <AcademySelect value={academy} onChange={setAcademy} />
          </div>

          {product.personalization?.length > 0 && (
            <Personalization fields={product.personalization} value={custom} onChange={setCustom} />
          )}

          {/* Qty + add */}
          <div className="mt-6 flex items-center gap-4">
            <div className="flex items-center border border-white/15 rounded-sm">
              <button data-testid="qty-dec" onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-3 text-zinc-300 hover:text-yellow-400"><Minus className="h-4 w-4" /></button>
              <span data-testid="qty-value" className="w-10 text-center font-mono font-bold">{qty}</span>
              <button data-testid="qty-inc" onClick={() => setQty((q) => q + 1)} className="p-3 text-zinc-300 hover:text-yellow-400"><Plus className="h-4 w-4" /></button>
            </div>
            <button data-testid="add-to-cart-btn" onClick={handleAdd} className="btn-primary flex-1 px-6 py-3.5 rounded-sm inline-flex items-center justify-center gap-2">
              <Check className="h-4 w-4" /> Add To Cart · {money(unitPrice * qty)}
            </button>
          </div>

          <p className="text-zinc-300 mt-6">{product.description}</p>
          {product.features?.length > 0 && (
            <ul className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
              {product.features.map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-zinc-400"><Check className="h-3.5 w-3.5 text-emerald-400 mt-0.5 shrink-0" /> {f}</li>
              ))}
            </ul>
          )}
          <p className="text-zinc-500 text-sm mt-4">{product.materials}</p>
          <div data-testid="size-guide" className="mt-6 border border-white/10 rounded-lg p-4">
            <p className="font-mono text-xs uppercase tracking-widest text-zinc-400">Size Guide</p>
            <p className="text-sm text-zinc-300 mt-1">Available sizes: {sizes.join(", ")}</p>
            <p className="text-xs text-zinc-500 mt-1">{product.size_chart}</p>
          </div>
        </div>
      </div>
      <RelatedProducts product={product} />
    </div>
  );
}
